import { useState, useEffect, useRef, useCallback, FormEvent } from 'react';
import { ShieldCheck, Mail, ArrowLeft } from 'lucide-react';
import { LoginOtpChallenge } from '../../contexts/AuthContext';
import { ButtonWithIcon } from '../ui/button';
import { OtpInput } from './OtpInput';
import { useResendTimer } from '../../hooks/useResendTimer';

interface LoginOtpVerifyStepProps {
  challenge: LoginOtpChallenge;
  onVerify: (challengeId: string, otpCode: string) => Promise<void>;
  onResend: (challengeId: string) => Promise<{ challengeId: string; maskedEmail: string; expiresInSeconds: number } | null>;
  onBack: () => void;
  error?: string;
}

export default function LoginOtpVerifyStep({ challenge, onVerify, onResend, onBack, error }: LoginOtpVerifyStepProps) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(challenge.expiresInSeconds);
  const [localError, setLocalError] = useState('');
  const [currentChallenge, setCurrentChallenge] = useState(challenge);
  const resendTimer = useResendTimer(30);
  const autoSubmitRef = useRef(false);

  // Reset countdown when challenge changes
  useEffect(() => {
    setCurrentChallenge(challenge);
    setSecondsLeft(challenge.expiresInSeconds);
  }, [challenge]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleSubmit = useCallback(async (e?: FormEvent) => {
    e?.preventDefault();
    if (otp.length !== 6 || loading) return;
    setLocalError('');
    setLoading(true);
    try {
      await onVerify(currentChallenge.challengeId, otp);
    } finally {
      setLoading(false);
    }
  }, [otp, loading, currentChallenge.challengeId, onVerify]);

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (otp.length === 6 && !loading && !autoSubmitRef.current) {
      autoSubmitRef.current = true;
      handleSubmit();
    }
    if (otp.length < 6) {
      autoSubmitRef.current = false;
    }
  }, [otp, loading, handleSubmit]);

  const handleResend = async () => {
    if (resendTimer.isActive || resending) return;
    setResending(true);
    setOtp('');
    setLocalError('');
    try {
      const result = await onResend(currentChallenge.challengeId);
      if (result) {
        setCurrentChallenge({
          challengeId: result.challengeId,
          maskedEmail: result.maskedEmail,
          expiresInSeconds: result.expiresInSeconds,
        });
        setSecondsLeft(result.expiresInSeconds);
      }
      resendTimer.start();
    } finally {
      setResending(false);
    }
  };

  const displayError = error || localError;
  const isExpired = secondsLeft <= 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Icon & header */}
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-soft dark:bg-primary/10">
          <ShieldCheck className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-base font-semibold text-foreground">Verify your login</h3>
        <p className="text-sm text-muted-foreground">
          A verification code was sent via{' '}
          <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> email</span>
          {' '}to <span className="font-medium text-foreground">{currentChallenge.maskedEmail}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {/* OTP input */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-foreground">
            Verification code
          </label>
          <OtpInput
            value={otp}
            onChange={setOtp}
            error={!!displayError}
            disabled={loading || isExpired}
          />
          {/* Countdown */}
          <p className={`text-xs text-right ${isExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
            {isExpired ? 'Code expired' : `Expires in ${formatCountdown(secondsLeft)}`}
          </p>
        </div>

        {displayError && (
          <p className="text-sm text-destructive text-center" role="alert">{displayError}</p>
        )}

        <ButtonWithIcon
          type="submit"
          icon={ShieldCheck}
          disabled={loading || otp.length !== 6 || isExpired}
          className="w-full"
        >
          {loading ? 'Verifying...' : 'Verify code'}
        </ButtonWithIcon>
      </form>

      {/* Standard bottom actions */}
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          Didn't receive code?{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || resendTimer.isActive}
            className="text-primary hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendTimer.isActive ? `Resend in ${resendTimer.seconds}s` : resending ? 'Sending...' : 'Resend'}
          </button>
        </p>
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to login
        </button>
      </div>
    </div>
  );
}
